import { Component } from '@theme/component'
import { debounce } from '@theme/maths'
import { useIntersection } from '@theme/use-intersection'

const AccordionCarouselConfig = {
  body: '.accordion-carousel-item__body',
  list: '.accordion-carousel__list',
  panel: '.accordion-carousel-item__panel',
  panelProperty: 'grid-template-rows',
  progressFill: 'accordion-carousel-item__progress-fill',
  stage: '.accordion-carousel__stage',
  reserve: '--accordion-carousel-reserve',
  stageHeight: '--accordion-carousel-stage-height',
  measureDelay: 150
}

class AccordionCarousel extends Component {
  #activeIndex = 0
  #stopObserving = null
  #resizeObserver = null
  #observedWidth = 0
  #reserved = 0

  /** @param {AnimationEvent} event */
  #advance = (event) => {
    const { target } = event
    if (!(target instanceof Element)) {
      return
    }
    if (!target.classList.contains(AccordionCarouselConfig.progressFill)) {
      return
    }
    this.next()
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const index = this.items.indexOf(/** @type {HTMLElement} */ (event.target))
    if (index < 0) {
      return
    }
    this.openItem(index)
  }

  /** @param {TransitionEvent} event */
  #onPanelSettled = (event) => {
    if (event.propertyName !== AccordionCarouselConfig.panelProperty) {
      return
    }
    this.#reservePanelSpace()
  }

  /** @param {IntersectionObserverEntry} entry */
  #onVisibilityChange = (entry) => {
    this.toggleAttribute('data-offscreen', !entry.isIntersecting)
  }

  /** @param {ResizeObserverEntry[]} entries */
  #onResize = (entries) => {
    const width = Math.round(entries[0]?.contentRect.width ?? 0)
    if (width === this.#observedWidth) {
      return
    }
    this.#observedWidth = width
    this.#reserved = 0
    this.#scheduleMeasure()
  }

  #scheduleMeasure = debounce(() => {
    this.#measure()
  }, AccordionCarouselConfig.measureDelay)

  connectedCallback() {
    super.connectedCallback()
    this.openItem(this.#initialIndex())
    this.addEventListener('animationend', this.#advance)
    this.addEventListener('transitionend', this.#onPanelSettled)
    this.addEventListener('shopify:block:select', this.#onBlockSelect)
    this.#stopObserving = useIntersection({ threshold: 0 }).observe(
      this,
      this.#onVisibilityChange
    )
    this.#watchWidth()
    requestAnimationFrame(this.#scheduleMeasure)
    document.fonts?.ready?.then(this.#scheduleMeasure)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('animationend', this.#advance)
    this.removeEventListener('transitionend', this.#onPanelSettled)
    this.removeEventListener('shopify:block:select', this.#onBlockSelect)
    this.#stopObserving?.()
    this.#scheduleMeasure.cancel()
    this.#resizeObserver?.disconnect()
    this.#resizeObserver = null
  }

  /** @returns {HTMLElement[]} */
  get items() {
    return this.refs.items ?? []
  }

  /** @returns {HTMLButtonElement[]} */
  get triggers() {
    return this.refs.triggers ?? []
  }

  /** @returns {number} */
  get activeIndex() {
    return this.#activeIndex
  }

  /** @param {MouseEvent} event */
  select(event) {
    const index = this.triggers.indexOf(
      /** @type {HTMLButtonElement} */ (event.target)
    )
    if (index < 0) {
      return
    }
    this.openItem(index)
  }

  togglePlayback() {
    const isPaused = this.toggleAttribute('data-paused')
    const { playback } = this.refs
    if (!(playback instanceof HTMLElement)) {
      return
    }
    const label = isPaused
      ? playback.dataset.labelPlay
      : playback.dataset.labelPause
    playback.setAttribute('aria-label', label ?? '')
  }

  /** @param {number} index */
  openItem(index) {
    const items = this.items
    if (!items[index]) {
      return
    }
    this.#activeIndex = index
    for (const [position, item] of items.entries()) {
      const isActive = position === index
      item.toggleAttribute('data-active', isActive)
      this.triggers[position]?.setAttribute('aria-expanded', String(isActive))
    }
    this.#syncCounter()
  }

  previous() {
    this.#step(-1)
  }

  next() {
    this.#step(1)
  }

  /** @returns {number} */
  #initialIndex() {
    return Math.max(
      this.items.findIndex((item) => item.hasAttribute('data-active')),
      0
    )
  }

  /** @param {number} offset */
  #step(offset) {
    const count = this.items.length
    if (count === 0) {
      return
    }
    this.openItem((this.#activeIndex + offset + count) % count)
  }

  #syncCounter() {
    const { counter } = this.refs
    if (!(counter instanceof HTMLElement)) {
      return
    }
    counter.textContent = String(this.#activeIndex + 1)
  }

  #watchWidth() {
    this.#measure()
    if (typeof ResizeObserver !== 'function') {
      return
    }
    this.#resizeObserver = new ResizeObserver(this.#onResize)
    this.#resizeObserver.observe(this)
  }

  #measure() {
    if (!this.isConnected) {
      return
    }
    this.#publishStageHeight()
    this.#reservePanelSpace()
  }

  #publishStageHeight() {
    const stage = this.querySelector(AccordionCarouselConfig.stage)
    const height =
      stage instanceof HTMLElement
        ? Math.round(stage.getBoundingClientRect().height)
        : 0
    if (height <= 0) {
      this.style.removeProperty(AccordionCarouselConfig.stageHeight)
      return
    }
    this.style.setProperty(AccordionCarouselConfig.stageHeight, `${height}px`)
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #bodyHeight(item) {
    const body = item.querySelector(AccordionCarouselConfig.body)
    return body instanceof HTMLElement ? body.scrollHeight : 0
  }

  /** @returns {boolean} */
  #isSettled() {
    return this.items.every((item, index) => {
      const panel = item.querySelector(AccordionCarouselConfig.panel)
      const height = panel?.getBoundingClientRect().height ?? 0
      const target = index === this.#activeIndex ? this.#bodyHeight(item) : 0
      return Math.abs(height - target) < 1
    })
  }

  #reservePanelSpace() {
    const list = this.querySelector(AccordionCarouselConfig.list)
    if (!(list instanceof HTMLElement && this.#isSettled())) {
      return
    }
    const bodies = this.items.map((item) => this.#bodyHeight(item))
    const tallest = Math.max(0, ...bodies)
    list.style.removeProperty(AccordionCarouselConfig.reserve)
    if (tallest === 0) {
      this.#reserved = 0
      return
    }
    const spare = tallest - (bodies[this.#activeIndex] ?? 0)
    const reserve = Math.ceil(list.getBoundingClientRect().height + spare)
    this.#reserved = Math.max(this.#reserved, reserve)
    list.style.setProperty(
      AccordionCarouselConfig.reserve,
      `${this.#reserved}px`
    )
  }
}

if (!customElements.get('accordion-carousel')) {
  customElements.define('accordion-carousel', AccordionCarousel)
}
